import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Loader2, Sparkles, RefreshCw, CheckCircle2, AlertTriangle, Filter, ExternalLink, FileWarning, Copy, User } from "lucide-react";
import SourceDetailModal from "@/components/SourceDetailModal";

const BATCH_MAX = 20;

const ENRICH_STATUS_OPTIONS = [
  { value: "PENDENTE_DE_ENRIQUECIMENTO", label: "Pendente IA", badge: "badge-warn" },
  { value: "ENRIQUECIDO", label: "Enriquecida", badge: "badge-success" },
  { value: "FALHA_ENRIQUECIMENTO", label: "Falha IA", badge: "badge-danger" },
  { value: "INSUFICIENTE", label: "Insuficiente", badge: "badge-danger" },
];

const OUTCOME_LABELS = {
  enriched: { label: "Enriquecido", color: "badge-success", icon: CheckCircle2 },
  duplicate: { label: "Já existia", color: "badge-info", icon: Copy },
  insufficient: { label: "Texto insuficiente", color: "badge-warn", icon: FileWarning },
  ai_error: { label: "Falha na IA", color: "badge-danger", icon: AlertTriangle },
  not_eligible: { label: "Não elegível", color: "badge-warn", icon: AlertTriangle },
  not_found: { label: "Não encontrada", color: "badge-danger", icon: AlertTriangle },
};

function statusBadge(value) {
  const opt = ENRICH_STATUS_OPTIONS.find((o) => o.value === value);
  return <span className={`badge ${opt?.badge || "badge-warn"} text-[10px]`}>{opt?.label || value || "—"}</span>;
}

export default function EnrichmentPanel({ onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("PENDENTE_DE_ENRIQUECIMENTO");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({}); // source_id -> bool
  const [busy, setBusy] = useState(false);          // batch in progress
  const [singleBusyId, setSingleBusyId] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState(null); // {summary, results}
  const [err, setErr] = useState("");
  const [openSource, setOpenSource] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (filter) params.append("status_enriquecimento", filter);
      if (search.trim()) params.append("q", search.trim());
      const { data } = await api.get(`/legacy-import/sources?${params.toString()}`);
      setItems(data.items || []);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(load, 100);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSelect = (sid) => setSelected((s) => ({ ...s, [sid]: !s[sid] }));

  const visibleSelectableIds = useMemo(
    () => items.filter((it) => it.status_enriquecimento === "PENDENTE_DE_ENRIQUECIMENTO" || it.status_enriquecimento === "FALHA_ENRIQUECIMENTO").map((it) => it.source_id),
    [items],
  );

  const selectedCount = useMemo(
    () => visibleSelectableIds.filter((sid) => selected[sid]).length,
    [selected, visibleSelectableIds],
  );

  const toggleAll = (checked) => {
    const limit = Math.min(visibleSelectableIds.length, BATCH_MAX);
    const next = { ...selected };
    if (checked) {
      visibleSelectableIds.slice(0, limit).forEach((sid) => { next[sid] = true; });
    } else {
      visibleSelectableIds.forEach((sid) => { delete next[sid]; });
    }
    setSelected(next);
  };

  const enrichOne = async (sid) => {
    if (singleBusyId || busy) return;
    setSingleBusyId(sid); setErr(""); setResults(null);
    try {
      const { data } = await api.post(`/legacy-import/enrich/${sid}`);
      setResults({ summary: { total: 1, [data.outcome === "enriched" ? "enriched" : data.outcome + "s"]: 1 }, results: [data] });
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSingleBusyId(null);
    }
  };

  const enrichBatch = async () => {
    const ids = visibleSelectableIds.filter((sid) => selected[sid]).slice(0, BATCH_MAX);
    if (ids.length === 0) {
      setErr("Selecione ao menos 1 registro pendente para enriquecer.");
      return;
    }
    if (!window.confirm(`Enviar ${ids.length} registro(s) ao Gemini para enriquecimento? Nenhum será aprovado automaticamente — todos irão para Revisão Humana.`)) return;
    setBusy(true); setErr(""); setResults(null);
    setProgress({ done: 0, total: ids.length });
    try {
      // Backend já é concorrente (semáforo 4). Aqui apenas chamamos uma vez e
      // mostramos o progresso ao final. Para feedback parcial granular,
      // disparamos micro-batches sequenciais de tamanho 4 para refletir progresso.
      const CHUNK = 4;
      const all = [];
      for (let i = 0; i < ids.length; i += CHUNK) {
        const slice = ids.slice(i, i + CHUNK);
        const { data } = await api.post("/legacy-import/enrich-batch", { source_ids: slice });
        all.push(...(data.results || []));
        setProgress({ done: Math.min(i + slice.length, ids.length), total: ids.length });
      }
      // resumo agregado
      const summary = {
        total: all.length,
        enriched: all.filter((r) => r.outcome === "enriched").length,
        duplicates: all.filter((r) => r.outcome === "duplicate").length,
        insufficient: all.filter((r) => r.outcome === "insufficient").length,
        ai_errors: all.filter((r) => r.outcome === "ai_error").length,
        not_eligible: all.filter((r) => r.outcome === "not_eligible").length,
        not_found: all.filter((r) => r.outcome === "not_found").length,
      };
      setResults({ summary, results: all });
      setSelected({});
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="enrich-panel" translate="no">
      <div className="card-mil p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="label-mil text-[var(--gold)]">Fase 2 · Enriquecimento com IA</div>
            <h2 className="font-display text-xl font-bold mt-1 flex items-center gap-2">
              <Sparkles size={18} className="text-[var(--gold)]" /> Base já importada
            </h2>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Aplica Gemini sobre cada registro legado e gera uma Semente em status
              <span className="badge badge-warn mx-1">Pendente</span>
              para Revisão Humana. Limite por lote: <span className="font-mono">{BATCH_MAX}</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="input-mil text-xs"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="enrich-filter-status"
            >
              {ENRICH_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
              <option value="">(todos)</option>
            </select>
            <div className="relative">
              <Filter size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
              <input
                className="input-mil pl-7 text-xs"
                placeholder="Buscar título / tema / responsável"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="enrich-search"
              />
            </div>
            <button type="button" onClick={load} className="btn-ghost text-xs flex items-center gap-1" data-testid="enrich-reload">
              <RefreshCw size={12} /> Atualizar
            </button>
            <button
              type="button"
              onClick={enrichBatch}
              disabled={busy || selectedCount === 0}
              className="btn-gold text-xs flex items-center gap-1 disabled:opacity-50"
              data-testid="enrich-batch-btn"
            >
              {busy ? <><Loader2 size={12} className="animate-spin" /> Enriquecendo {progress.done}/{progress.total}…</> : <><Sparkles size={12} /> Enriquecer selecionadas ({selectedCount})</>}
            </button>
          </div>
        </div>
        {selectedCount > BATCH_MAX && (
          <div className="text-[11px] text-[var(--warn)] mt-2">Apenas as primeiras {BATCH_MAX} selecionadas serão enviadas.</div>
        )}
      </div>

      {err && <div className="text-sm text-[var(--danger)] border border-[var(--danger)]/40 bg-[var(--danger)]/10 p-3 rounded" data-testid="enrich-err">{err}</div>}

      <div className="card-mil overflow-hidden">
        <table className="w-full text-sm" data-testid="enrich-table">
          <thead>
            <tr>
              <th className="px-3 py-2 border-b border-[var(--border)] w-8 text-center">
                <input
                  type="checkbox"
                  disabled={visibleSelectableIds.length === 0}
                  checked={visibleSelectableIds.length > 0 && visibleSelectableIds.slice(0, BATCH_MAX).every((sid) => selected[sid])}
                  onChange={(e) => toggleAll(e.target.checked)}
                  data-testid="enrich-check-all"
                />
              </th>
              {["ID", "Título", "Tema", "Ano", "Inserido por", "Status Enriq.", "Confiança", "Ações"].map((h) => (
                <th key={h} className="px-3 py-2 label-mil border-b border-[var(--border)] text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center p-6 text-[var(--text-muted)] text-sm"><Loader2 className="animate-spin inline mr-2" size={14} /> Carregando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={9} className="text-center p-6 text-[var(--text-muted)] text-sm">Nenhum registro neste filtro.</td></tr>
            )}
            {!loading && items.map((it) => {
              const eligible = it.status_enriquecimento === "PENDENTE_DE_ENRIQUECIMENTO" || it.status_enriquecimento === "FALHA_ENRIQUECIMENTO";
              return (
                <tr
                  key={it.source_id}
                  className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] cursor-pointer"
                  onClick={() => setOpenSource(it.source_id)}
                  data-testid={`enrich-row-${it.source_id}`}
                >
                  <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                    {eligible && (
                      <input
                        type="checkbox"
                        checked={!!selected[it.source_id]}
                        onChange={() => toggleSelect(it.source_id)}
                        data-testid={`enrich-check-${it.source_id}`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{it.legacy_id ?? "—"}</td>
                  <td className="px-3 py-2 text-xs max-w-md">
                    <div className="truncate font-semibold" title={it.titulo}>{it.titulo}</div>
                    <div className="text-[10px] text-[var(--text-muted)] font-mono">{it.source_id}</div>
                    {it.last_enrichment_error && (
                      <div className="text-[10px] text-[var(--danger)] mt-0.5 truncate" title={it.last_enrichment_error}>Erro: {it.last_enrichment_error}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--text-muted)] max-w-[160px] truncate" title={it.tema_legado}>{it.tema_legado || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">{it.ano_publicacao ?? "—"}</td>
                  <td className="px-3 py-2 text-xs max-w-[140px] truncate">
                    <span className="inline-flex items-center gap-1">
                      <User size={10} className="text-[var(--text-muted)]" />
                      <span title={it.imported_by_user_name || it.created_by_user_name || ""}>
                        {it.imported_by_user_name || it.created_by_user_name || <span className="text-[var(--text-muted)]">—</span>}
                      </span>
                    </span>
                  </td>
                  <td className="px-3 py-2">{statusBadge(it.status_enriquecimento)}</td>
                  <td className="px-3 py-2 font-mono text-xs" onClick={(e) => e.stopPropagation()}>{it.enriched_seed_id ? <a href={`/revisao`} className="text-[var(--gold)] inline-flex items-center gap-1">ver semente <ExternalLink size={10} /></a> : "—"}</td>
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    {eligible ? (
                      <button
                        type="button"
                        onClick={() => enrichOne(it.source_id)}
                        disabled={busy || singleBusyId === it.source_id}
                        className="btn-ghost text-[11px] flex items-center gap-1 disabled:opacity-50"
                        data-testid={`enrich-one-${it.source_id}`}
                      >
                        {singleBusyId === it.source_id ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                        Enriquecer
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {results && (
        <div className="card-mil p-4 gold-accent-top" data-testid="enrich-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={18} className="text-[var(--success)]" />
            <div className="font-display text-base font-bold">Resumo do processamento</div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-center">
            {[
              { k: "enriched", lbl: "Enriquecidas", color: "text-[var(--success)]" },
              { k: "duplicates", lbl: "Já existiam", color: "text-[var(--info)]" },
              { k: "insufficient", lbl: "Insuficiente", color: "text-[var(--warn)]" },
              { k: "ai_errors", lbl: "Falha IA", color: "text-[var(--danger)]" },
              { k: "not_eligible", lbl: "Não elegível", color: "text-[var(--text-muted)]" },
              { k: "not_found", lbl: "Não encontrada", color: "text-[var(--text-muted)]" },
            ].map((s) => (
              <div key={s.k} className="border border-[var(--border)] rounded p-2">
                <div className={`font-mono text-xl ${s.color}`}>{results.summary?.[s.k] || 0}</div>
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{s.lbl}</div>
              </div>
            ))}
          </div>

          {results.results?.length > 0 && (
            <div className="mt-3 max-h-60 overflow-auto">
              <table className="w-full text-xs" data-testid="enrich-result-table">
                <thead>
                  <tr>
                    <th className="text-left label-mil px-2 py-1">ID</th>
                    <th className="text-left label-mil px-2 py-1">Título</th>
                    <th className="text-left label-mil px-2 py-1">Resultado</th>
                    <th className="text-left label-mil px-2 py-1">Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((r) => {
                    const meta = OUTCOME_LABELS[r.outcome] || OUTCOME_LABELS.ai_error;
                    const Icon = meta.icon;
                    return (
                      <tr key={r.source_id} className="border-t border-[var(--border)]/40">
                        <td className="px-2 py-1 font-mono">{r.legacy_id ?? "—"}</td>
                        <td className="px-2 py-1 max-w-md truncate" title={r.titulo}>{r.titulo || r.source_id}</td>
                        <td className="px-2 py-1"><span className={`badge ${meta.color} text-[10px] flex items-center gap-1 w-fit`}><Icon size={10} /> {meta.label}</span></td>
                        <td className="px-2 py-1 text-[var(--text-muted)] max-w-md truncate" title={r.message || r.classificacao_semente}>
                          {r.outcome === "enriched"
                            ? `Classificação ${r.classificacao_semente} · confiança ${r.confianca_ia}%`
                            : r.message || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <SourceDetailModal sourceId={openSource} onClose={() => setOpenSource(null)} />
    </div>
  );
}
